import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import '../../styles/sitePages.css';
import './AppDetailPage.css';
import { appStoreUrl, playStoreUrl, trackStoreClick } from '../../utils/storeLinks';

function AquariumAppPage() {
  const reviews = [
    {
      author: 'Puzzle Enthusiast',
      rating: '★★★★★',
      text: "Such a relaxing yet challenging puzzle game. The water-filling mechanic is unique and the puzzles are very well designed.",
      source: 'App Store'
    },
    {
      author: 'Logic Fan',
      rating: '★★★★★',
      text: "Finally found a puzzle game that respects my intelligence. No guessing needed, just pure logical deduction. Beautiful!",
      source: 'Google Play'
    },
    {
      author: 'Daily Player',
      rating: '★★★★☆',
      text: "Great for winding down. The difficulty progression is smooth and the visuals are clean. Highly recommend.",
      source: 'App Store'
    }
  ];

  const features = [
    { title: 'Unique Mechanic', description: 'Fill aquariums with water based on row and column counts - water settles naturally.' },
    { title: 'Hand-Curated Levels', description: 'Every puzzle is designed and tested for a smooth, satisfying solving experience.' },
    { title: 'Fair Deductions', description: 'Every board is solvable without guessing - pure logic from start to finish.' },
    { title: 'Stunning Animations', description: 'Watch the water fill with beautiful, satisfying animations.' },
    { title: 'Multiple Grid Sizes', description: 'From beginner-friendly 6x6 grids to challenging larger puzzles.' },
    { title: 'Hint System', description: 'Stuck? Get a gentle nudge in the right direction without spoiling the solve.' }
  ];

  return (
    <div className="app-detail-page">
      <Helmet>
        <title>Aquarium Logic Puzzle - Mobile App for iOS & Android | Logic Puzzles</title>
        <meta name="description" content="Download Aquarium Logic Puzzle for iOS and Android. 4.6-star App Store rating. A captivating water-filling puzzle game with beautiful animations." />
        <link rel="canonical" href="https://logicpuzzles.ca/apps/aquarium" />
        <meta property="og:title" content="Aquarium Logic Puzzle - Mobile App for iOS & Android" />
        <meta property="og:description" content="Download Aquarium Logic Puzzle. 4.6-star App Store rating. A captivating water-filling puzzle game." />
        <meta property="og:url" content="https://logicpuzzles.ca/apps/aquarium" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Aquarium Logic Puzzle - Mobile App for iOS & Android" />
        <meta name="twitter:description" content="Download Aquarium Logic Puzzle. 4.6-star App Store rating. A captivating water-filling puzzle game." />
        <meta name="twitter:image" content="https://logicpuzzles.ca/og-image.png" />
      </Helmet>

      <NavBar />

      <section className="app-detail-hero">
        <div className="container app-detail-hero-content">
          <div className="app-detail-hero-copy">
            <span className="eyebrow">Mobile app · iOS &amp; Android</span>
            <h1>Aquarium Logic Puzzle</h1>
            <p className="app-tagline">Water logic with zero guessing</p>

            <div className="app-detail-stats">
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">4.6 ★</span>
                <span className="app-detail-stat-label">Rating</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">4K+</span>
                <span className="app-detail-stat-label">Downloads</span>
              </div>
              <div className="app-detail-stat">
                <span className="app-detail-stat-value">500+</span>
                <span className="app-detail-stat-label">Puzzles</span>
              </div>
            </div>

            <div className="app-detail-buttons">
              <a href={appStoreUrl('aquarium', 'apps-page', '/apps/aquarium')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'aquarium', store: 'app_store', placement: 'apps-page', page: '/apps/aquarium' })}>
                <img src="/app-store-badge.svg" alt="Download on the App Store" />
              </a>
              <a href={playStoreUrl('aquarium', 'apps-page', '/apps/aquarium')} target="_blank" rel="noopener" onClick={() => trackStoreClick({ app: 'aquarium', store: 'google_play', placement: 'apps-page', page: '/apps/aquarium' })}>
                <img src="/play-store-badge.png" alt="Get it on Google Play" />
              </a>
            </div>
          </div>
          <div className="app-detail-hero-art">
            <img
              src="/assets/logos/aqua_logo.webp"
              alt="Aquarium Logic Puzzle App Icon"
              className="app-detail-icon"
              width="200"
              height="200"
            />
          </div>
        </div>
      </section>

      <div className="app-detail-content">
        <section className="app-detail-section">
          <h2>About Aquarium</h2>
          <p className="app-detail-description">
            Aquarium is a captivating logic puzzle game where you strategically fill aquariums to specific water levels, 
            guided by numerical clues. Water settles naturally - if a cell is filled, everything below it in the same 
            compartment fills too. Use row and column counts to deduce the perfect water levels.
          </p>
        </section>

        <section className="app-detail-section">
          <h2>Features</h2>
          <div className="app-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="app-feature-card">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section">
          <h2>How to Play Aquarium</h2>
          <p className="app-detail-description">
            The grid is divided into irregularly shaped aquariums (tanks). Numbers along the top and left edges
            indicate how many cells in each column and row must be filled with water. Water obeys gravity - if
            a cell in a tank is filled, every cell below it in the same tank fills automatically. Your task is
            to determine the exact water level in each tank so the row and column counts are satisfied.
          </p>
          <h3>Tips for New Players</h3>
          <ul className="app-detail-list">
            <li>Start with rows or columns that require 0 or the maximum number of filled cells - these are fully determined.</li>
            <li>If filling a tank's bottom row would exceed a column count, that tank must stay empty at that level.</li>
            <li>Use the row and column counts together to cross-reference which tanks can or cannot hold water.</li>
            <li>Remember that water levels within a single tank must be uniform across all of its columns.</li>
          </ul>
        </section>

        <section className="app-detail-section app-reviews-section">
          <h2>What Players Are Saying</h2>
          <div className="app-reviews-grid">
            {reviews.map((review, index) => (
              <div key={index} className="app-review-card">
                <div className="app-review-header">
                  <span className="app-review-author">{review.author}</span>
                  <span className="app-review-rating">{review.rating}</span>
                </div>
                <p className="app-review-text">"{review.text}"</p>
                <span className="app-review-source">{review.source}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="app-detail-section app-cta-section sp-cta">
          <div>
            <h2>Ready to Play?</h2>
            <p>Download Aquarium and dive into water-filling puzzles!</p>
          </div>
          <div className="app-cta-buttons sp-cta__actions">
            <Link to="/play/aquarium" className="button spark">
              Play Free Online
            </Link>
            <Link to="/apps" className="button sp-on-dark">
              ← Back to All Apps
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default AquariumAppPage;
